/* eslint-disable global-require */
import React, { useContext, useState } from 'react'
import { IconSolidInfo, IconRegularPlay } from '@notino/react-styleguide'

// components
import { useIntl } from 'react-intl'
import StoreDownloadButtons from '../../../../components/StoreDownloadButtons/StoreDownloadButtons'
import VideoModal from '../../../../components/VideoModal/VideoModal'
import Modal from '../../../../atoms/Modal/Modal'

// utils
import { AppContext } from '../../../../utils/appProvider'

// styles
import * as SC from './AppPromoSectionStyles'
import { BREAKPOINTS_INTS } from '../../../../styles/constants'
import { HomePageContainer } from '../../HomePageStyles'

// hooks
import useMessages from '../../../../hooks/useMessages'

// types
import { CmsAppPromoData } from '../../../../types/types'
import { getLinkAttributes } from '../../../../utils/helper'

type Props = {
	cmsData: CmsAppPromoData
	onPlayClick?: (name?: string) => void
}

// component
const AppPromoSection = (props: Props) => {
	const { cmsData, onPlayClick } = props
	const { tooltip, videoThumbnail, video, background, title, subtitle } = cmsData

	const { locale } = useIntl()
	const { assetsPath, productionEnvironment } = useContext(AppContext)

	const { messages } = useMessages()

	const [visibleInfoBanner, setVisibleInfoBanner] = useState(false)
	const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

	const qrCode = `${assetsPath}/${require('../../../../assets/images/app-qr-code.jpg')}`
	const qrCodeWebP = `${assetsPath}/${require('../../../../assets/images/app-qr-code.webp')}`

	const videoThumbnailPicture = (
		<SC.Picture>
			{/* WebP variants */}
			{videoThumbnail.desktopWebP && <source srcSet={videoThumbnail.desktopWebP} type='image/webp' media={`(min-width: ${BREAKPOINTS_INTS.sm}px)`} />}
			{videoThumbnail.mobileWebP && <source srcSet={videoThumbnail.mobileWebP} type='image/webp' media={`(max-width: ${BREAKPOINTS_INTS.sm - 1}px)`} />}

			{/* JPEG fallback */}
			<source srcSet={videoThumbnail.desktop} media={`(min-width: ${BREAKPOINTS_INTS.sm}px)`} />
			<source srcSet={videoThumbnail.mobile} media={`(max-width: ${BREAKPOINTS_INTS.sm - 1}px)`} />

			{/* Fallback <img> tag for maximum compatibility */}
			<img src={videoThumbnail.desktop} alt={videoThumbnail.alt} loading='lazy' width={640} height={400} />
		</SC.Picture>
	)

	const getImageContent = () => {
		if (video?.url) {
			return (
				<SC.VideoPreviewButton
					onClick={() => {
						if (onPlayClick) {
							onPlayClick(video.name)
						}
						setIsVideoModalOpen(true)
					}}
				>
					<SC.PictureContainer>{videoThumbnailPicture}</SC.PictureContainer>
					<SC.PlayIcon>
						<IconRegularPlay color={'icon.inverse'} />
					</SC.PlayIcon>
				</SC.VideoPreviewButton>
			)
		}

		if (videoThumbnail.link) {
			return (
				<SC.ImageLink href={videoThumbnail.link} {...getLinkAttributes(videoThumbnail.link, productionEnvironment, locale)}>
					{videoThumbnailPicture}
				</SC.ImageLink>
			)
		}

		return videoThumbnailPicture
	}

	return (
		<SC.AppPromoSection
			$mobileBg={background.mobile}
			$desktopBg={background.desktop}
			$mobileBgWebp={background.mobileWebP}
			$desktopBgWebp={background.desktopWebP}
		>
			<HomePageContainer>
				<SC.Layout>
					<SC.InfoCol>
						<SC.SectionTitle $marginBottom={'20px'}>{title}</SC.SectionTitle>
						<SC.SectionDescription $marginBottom={'20px'}>{subtitle}</SC.SectionDescription>
						<SC.DownloadsSectionWrapper>
							<SC.QRCodeImageWrapper>
								<picture>
									<source srcSet={qrCodeWebP} type='image/webp' />
									<img src={qrCode} width={86} height={86} alt={messages['Download Notino app via QR code']} loading='lazy' />
								</picture>
							</SC.QRCodeImageWrapper>

							<SC.StoreDownloadButtonsWrapper>
								<StoreDownloadButtons />
							</SC.StoreDownloadButtonsWrapper>
							{tooltip && (
								<SC.InfoIconButton
									type={'button'}
									aria-label={messages['Additional information']}
									aria-haspopup={'dialog'}
									onClick={() => setVisibleInfoBanner(true)}
								>
									<IconSolidInfo width={'1.25rem'} height={'1.25rem'} color={'icon.tertiary'} />
								</SC.InfoIconButton>
							)}
						</SC.DownloadsSectionWrapper>
					</SC.InfoCol>
					<SC.ImageCol>
						<SC.ImageContainer>{getImageContent()}</SC.ImageContainer>
					</SC.ImageCol>
				</SC.Layout>
			</HomePageContainer>
			{tooltip && visibleInfoBanner && (
				<Modal show header={messages.Information} ariaLabel={messages.Information} onClose={() => setVisibleInfoBanner(false)}>
					<SC.InfoModalText>{tooltip}</SC.InfoModalText>
				</Modal>
			)}
			{/* with and height should be set according to video provided by Notino marketing */}
			{isVideoModalOpen && video?.url && (
				<VideoModal show onClose={() => setIsVideoModalOpen(false)} videoSrc={video.url} width={video.width} height={video.height} />
			)}
		</SC.AppPromoSection>
	)
}

export default AppPromoSection
